import { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Phone, X } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { api, formatApiErrorDetail } from "../../lib/api";

const CALL_RESULT_OPTIONS = [
  "No Answer",
  "Left Voicemail",
  "Called Wrong Number",
  "Not Interested",
  "Interested — Schedule Callback",
  "Submitted Application",
  "Deal Closed",
  "Other",
];

export function DispositionModal({ lead, onComplete, onClose }) {
  const [result, setResult] = useState("");
  const [notes, setNotes] = useState("");
  const [callbackAt, setCallbackAt] = useState("");
  const [actualPhone, setActualPhone] = useState("");
  const [saving, setSaving] = useState(false);
  // Launch-day lead card audit (Wes, Sep 21 2026): when a rep calls through the app's Call
  // button, reveal-phone already logged the true dialed number — no need to ask again. But
  // "Log Disposition" (no Call click first) used to silently claim the dropdown's currently
  // selected number was dialed, even if the rep actually called from their own phone using a
  // completely different number found elsewhere. Now that case asks them to confirm/correct it.
  const needsPhoneConfirm = !lead.via_call_button;

  const submit = async () => {
    if (!result) { toast.error("Select a Call Result"); return; }
    if (!notes.trim()) { toast.error("Notes cannot be blank"); return; }
    if (result === "Interested — Schedule Callback" && !callbackAt) { toast.error("Pick a callback date and time"); return; }
    if (needsPhoneConfirm && !actualPhone.trim()) { toast.error("Enter the number you actually called"); return; }
    setSaving(true);
    try {
      await api.post(`/rep/leads/${lead.id}/disposition`, {
        result,
        notes: notes.trim(),
        callback_at: result === "Interested — Schedule Callback" ? new Date(callbackAt).toISOString() : null,
        phone_dialed: (needsPhoneConfirm ? actualPhone.trim() : lead.selected_phone) || lead.phone || null,
      });
      toast.success("Disposition saved");
      onComplete();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4" data-testid="disposition-modal-overlay">
      <div className="bg-igc-surface border border-igc-gold/30 max-w-md w-full p-6 relative" data-testid="disposition-modal">
        <button
          data-testid="disposition-modal-close-button"
          onClick={onClose || onComplete}
          aria-label="Close"
          className="absolute top-4 right-4 text-igc-muted hover:text-igc-cream"
        >
          <X size={18} />
        </button>
        <p className="text-igc-goldText text-xs uppercase tracking-widest mb-1 flex items-center gap-2">
          <Phone size={14} weight="fill" /> Call Disposition Required
        </p>
        <p className="font-serif text-xl text-igc-cream mb-1">{lead.name}</p>
        <p className="text-igc-muted text-xs mb-5">{lead.phone}</p>

        {needsPhoneConfirm && (
          <div className="mb-4">
            <label className="text-xs uppercase tracking-widest text-igc-muted mb-2 block" title="You logged this without clicking Call in the app, so we don't know for sure which number you actually dialed. Enter the real number you called from your own phone — this is what gets saved to the lead's record.">
              What number did you actually call? <span className="text-igc-danger">*</span>
            </label>
            <Input
              data-testid="disposition-actual-phone-input"
              value={actualPhone}
              onChange={(e) => setActualPhone(e.target.value)}
              placeholder="e.g. (555) 123-4567"
              title="The exact number you dialed from your own phone for this call."
              className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"
            />
          </div>
        )}

        <label className="text-xs uppercase tracking-widest text-igc-muted mb-2 block" title="What actually happened when you called or emailed this lead. You must pick one before you can move to the next lead.">Call Result <span className="text-igc-danger">*</span></label>
        <Select value={result} onValueChange={setResult}>
          <SelectTrigger data-testid="disposition-result-select" title="Choose the outcome that best matches this call: No Answer if they didn't pick up, Left Voicemail if you left a message, Called Wrong Number if the number was bad, Not Interested if they declined, Interested — Schedule Callback if they want you to call back later, Submitted Application if they agreed to move forward, or Deal Closed if the deal is done." className="w-full bg-igc-surfaceLight border-igc-gold/20 text-igc-cream mb-4">
            <SelectValue placeholder="Select a result" />
          </SelectTrigger>
          <SelectContent className="!z-[10050] bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
            {CALL_RESULT_OPTIONS.map((opt) => (
              <SelectItem key={opt} value={opt} data-testid={`disposition-result-option-${opt.replace(/\s/g, "-")}`}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <label className="text-xs uppercase tracking-widest text-igc-muted mb-2 block" title="A short written summary of what happened on the call. This is required and gets saved permanently on the lead's record so you (or anyone else who works this lead later) can see the history.">Notes <span className="text-igc-danger">*</span></label>
        <Textarea
          data-testid="disposition-notes-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="What happened on this call?"
          title="Type what happened on the call — this is required and becomes part of the lead's permanent record."
          className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream mb-4 min-h-[80px]"
        />

        {result === "Interested — Schedule Callback" && (
          <div className="mb-4">
            <label className="text-xs uppercase tracking-widest text-igc-muted mb-2 block" title="Pick the exact date and time you promised to call this lead back. It will automatically show up on your Callback Calendar tab as a reminder.">Callback Date & Time <span className="text-igc-danger">*</span></label>
            <Input
              data-testid="disposition-callback-datetime-input"
              type="datetime-local"
              value={callbackAt}
              onChange={(e) => setCallbackAt(e.target.value)}
              title="The date and time you promised to call this lead back — it will show up on your Callback Calendar."
              className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"
            />
          </div>
        )}

        <Button data-testid="disposition-submit-button" onClick={submit} disabled={saving} title="Saves this call result and notes to the lead's record permanently, and closes this window." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">
          {saving ? "Saving…" : "Save Disposition"}
        </Button>
        <p className="text-igc-muted text-[11px] mt-3 text-center">You must log a result before moving to the next lead.</p>
      </div>
    </div>,
    document.body
  );
}
